import React from "react";
import { Link } from "react-router-dom";
import { Gem } from "lucide-react";
import { useProducts } from "@/hooks/useProducts";
import { MediaTile } from "@/components/MediaTile";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * "New Arrivals" homepage promo section — a wide banner strip with a badge
 * counting live offers, plus two big caption tiles below sourced from your
 * current Offers/Festival items. Everything links to the New Arrivals &
 * Offers page. Deliberately uses a plain gradient (not your uploaded
 * Homepage Banner photo) — that photo already appears prominently in its
 * own dedicated banner section, and often has its own text/graphics baked
 * in, so reusing it here would clash with this section's own heading.
 * Renders nothing if there's no offer data at all yet.
 */
export const NewArrivalsPromo = () => {
  const { items: offers, loading: loadingOffers } = useProducts("offers");
  const { items: festival, loading: loadingFestival } = useProducts("festival");

  const loading = loadingOffers || loadingFestival;
  const combined = [...offers, ...festival].filter((i) => i.is_active);
  const tiles = combined.slice(0, 2);

  if (!loading && combined.length === 0) return null;

  return (
    <section className="relative py-14 sm:py-20 bg-background" data-testid="new-arrivals-promo">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Wide banner strip — plain gradient, no photo, so its heading never clashes with other text */}
        <Link
          to="/new-arrivals"
          className="group relative block aspect-[21/9] sm:aspect-[3/1] rounded-2xl sm:rounded-3xl overflow-hidden mb-6 sm:mb-8"
        >
          <div className="absolute inset-0" style={{ background: "var(--gradient-dark)" }} />
          <div
            className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-40"
            style={{ background: "var(--gradient-radial-glow)" }}
            aria-hidden="true"
          />

          <div className="absolute inset-y-0 left-0 flex flex-col justify-center p-6 sm:p-10 lg:p-14">
            <div className="flex items-center gap-3">
              <h2 className="font-heading text-2xl sm:text-4xl font-semibold text-foreground">New Arrivals</h2>
              {combined.length > 0 && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-card border border-primary/30 px-3 py-1 text-xs font-medium text-primary">
                  <Gem className="h-3 w-3" /> {combined.length}+ New Items
                </span>
              )}
            </div>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-sm">
              Fresh festive collections &amp; offers, added regularly. Explore the latest right now!
            </p>
          </div>
        </Link>

        {/* Two caption tiles */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <Skeleton className="aspect-[16/10] rounded-2xl" />
            <Skeleton className="aspect-[16/10] rounded-2xl" />
          </div>
        ) : tiles.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {tiles.map((item) => (
              <Link
                key={item.id}
                to="/new-arrivals"
                className="group relative aspect-[16/10] rounded-2xl overflow-hidden"
                data-testid={`new-arrivals-tile-${item.id}`}
              >
                {item.media_url ? (
                  <MediaTile
                    src={item.media_url}
                    mediaType={item.media_type}
                    alt={item.name}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                ) : (
                  <div className="absolute inset-0" style={{ background: "var(--gradient-gold)" }} />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <span className="absolute bottom-5 left-6 font-heading text-xl sm:text-2xl font-semibold text-white">
                  {item.name}
                </span>
              </Link>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};
